import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
} from "@mui/material";
import axios from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { sortAlphabetically } from "../../utils/sortAlphabetically";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

export default function CustomerGroup({ device, disabled = false, deviceVersion, deviceLoading,
  onDeviceUpdated
}) {
  const [allGroups, setAllGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [originalGroup, setOriginalGroup] = useState("");
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch all customer groups
  useEffect(() => {
    const fetchGroups = async () => {
      setLoadingGroups(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/groups`,
          {
            params: {
              companyId: process.env.REACT_APP_COMPANY_ID,
              type: "customer",
            },
          }
        );

        const sortedData = sortAlphabetically(response.data, 'name');
        setAllGroups(sortedData);
      } catch (err) {
        console.error("Error fetching customer groups:", err);
        toast.error("Failed to load customer groups.");
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchGroups();
  }, []);

  // Initialize selected group from device assets
  useEffect(() => {
    if (!device?.assets) return;
    const customerAsset = device.assets.find(
      (a) => a.group?.type === "customer"
    );
    const groupId = customerAsset?.group?.id || "";

    setSelectedGroup(groupId);
    setOriginalGroup(groupId);
  }, [device, deviceVersion]);


  const handleUpdate = async () => {
    if (!device?.id) return;

    const normalize = (v) => (v === "" ? null : v);
    const noChange = normalize(selectedGroup) === normalize(originalGroup);
    if (noChange) {
      toast.info("No changes detected.");
      return;
    }

    setIsUpdating(true);
    try {
      const payload = {
        newAssets: selectedGroup ? [{ groupId: selectedGroup }] : [],
        oldAssets: originalGroup ? [{ groupId: originalGroup }] : [],
        UI_Description: `${getUIDescription()}\n "Update Button of the Customer Group"`, 
      };

      await axios.put(
        `${process.env.REACT_APP_API_GATEAWAY}/devices?id=${device.id}`,
        payload
      );

      setOriginalGroup(selectedGroup);
      toast.success("✅ Customer group updated successfully!");
      onDeviceUpdated()

    } catch (err) {
      console.error("Error updating customer group:", err);
      toast.error("Failed to update customer group.");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", position: "relative" }}>
      {loadingGroups ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress color="#60a5fa" size={60} />
        </Box>
      ) : (
        <Card
          sx={{
            backgroundColor: "#1E293B",
            border: "1px solid #2E3B4C",
            color: "#fff",
            borderRadius: 3,
            p: 3,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          {/* Top Section */}
          <Box>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Customer Group
            </Typography>

            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
              Select Customer Group
            </Typography>

            <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
              <Select
                value={selectedGroup || ""}
                onChange={(e) => setSelectedGroup(e.target.value)}
                disabled={disabled || loadingGroups}
                sx={{
                  backgroundColor: "#2E3B4C",
                  color: "#fff",
                  border: "1px solid #60a5fa",
                  borderRadius: 1,
                  "& .MuiSvgIcon-root": { color: "#fff" },
                }}
              >
                <MenuItem value="">No Group Selected</MenuItem>
                {allGroups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Bottom Button */}
          <Box sx={{ mt: 3 }}>
            <Button
              onClick={handleUpdate}
              disabled={isUpdating || deviceLoading}
              fullWidth
              sx={{
                backgroundColor: "#60a5fa",
                color: "#000",
                fontWeight: 600,
                borderRadius: 1,
                textTransform: "none",
                "&:hover": { backgroundColor: "#3b82f6" },
                "&:disabled": { backgroundColor: "#334155", color: "#94a3b8" },
              }}
            >
              {isUpdating ? "Updating..." : "Update"}
            </Button>
          </Box>
        </Card>
      )}

      {/* Overlay Loading Spinner */}
      {isUpdating && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(4px)",
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            zIndex: 5,
          }}
        >
          <CircularProgress sx={{ color: "#60a5fa", mb: 2 }} />
          <Typography variant="body2" fontWeight={500}>
            Updating customer group...
          </Typography>
        </Box>
      )}
    </Box>
  );
}
